import { useRef, useState } from "react";
import { compressFilesForUpload } from "../../common/compressImage";
import Form from "../../common/Form";
import { submitWorkerMultipartForm } from "../../common/formApi";
import PhoneInputField, {
  formatPhoneForSubmit,
  isCompletePhoneNumber,
} from "../../common/PhoneInputField";
import {
  applyDateInputValidity,
  applyEmailInputValidity,
  EMAIL_INPUT_PATTERN,
  getLocalDateString,
  isSelectableFormDate,
  isValidEmail,
} from "../../common/formUtils";
import "./OrderForm.scss";

const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_ATTACHMENTS = 8;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_UPLOAD_SIZE_BYTES = 30 * 1024 * 1024;

type SelectedUpload = {
  sourceKey: string;
  file: File;
};

function getSourceKey(file: File) {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

function OrderForm() {
  const [orderFormData, setOrderFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    date: "",
    celebrationTime: "",
    pickupDelivery: "Pickup",
    timeNeeded: "",
    deliveryAddress: "",
    subject: "",
    details: "",
    website: "",
  });
  const [selectedFiles, setSelectedFiles] = useState<SelectedUpload[]>([]);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [phoneResetKey, setPhoneResetKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const selectedFilesRef = useRef<SelectedUpload[]>([]);
  selectedFilesRef.current = selectedFiles;

  function handleOrderFormChange(
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) {
    const { name, value } = e.target;
    if (e.target instanceof HTMLInputElement) {
      if (e.target.type === "date") {
        applyDateInputValidity(e.target);
      }
      if (e.target.type === "email") {
        applyEmailInputValidity(e.target);
      }
    }
    setOrderFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "pickupDelivery" && value === "Pickup"
        ? { deliveryAddress: "" }
        : {}),
    }));
  }

  function validateFiles(files: File[], checkSize = true) {
    if (files.length > MAX_ATTACHMENTS) {
      return `Please upload up to ${MAX_ATTACHMENTS} files.`;
    }

    const hasInvalidType = files.some(
      (file) => !ACCEPTED_FILE_TYPES.includes(file.type),
    );
    if (hasInvalidType) {
      return "Allowed files: JPG, PNG, WEBP, PDF, DOC, DOCX.";
    }

    if (!checkSize) {
      return "";
    }

    const hasOversizedFile = files.some(
      (file) => file.size > MAX_FILE_SIZE_BYTES,
    );
    if (hasOversizedFile) {
      return "Each file must be 10MB or smaller.";
    }

    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_TOTAL_UPLOAD_SIZE_BYTES) {
      return "The total upload size must be 30MB or less.";
    }

    return "";
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newFiles = Array.from(e.target.files ?? []);
    // Let users pick the same file again after removing it.
    e.target.value = "";
    if (newFiles.length === 0) {
      return;
    }

    setSubmitError("");

    const previous = selectedFilesRef.current;
    const incoming = newFiles.filter(
      (file) =>
        !previous.some((selected) => selected.sourceKey === getSourceKey(file)),
    );
    if (incoming.length === 0) {
      return;
    }

    const typeError = validateFiles(
      [...previous.map((selected) => selected.file), ...incoming],
      false,
    );
    if (typeError) {
      setSubmitError(typeError);
      return;
    }

    setIsCompressing(true);
    try {
      const compressed = await compressFilesForUpload(incoming);
      const merged = [
        ...previous,
        ...incoming.map((file, index) => ({
          sourceKey: getSourceKey(file),
          file: compressed[index],
        })),
      ];
      const validationError = validateFiles(
        merged.map((selected) => selected.file),
      );
      if (validationError) {
        setSubmitError(validationError);
        return;
      }
      setSelectedFiles(merged);
    } catch {
      setSubmitError(
        "Could not process one of the selected images. Please try a different file.",
      );
    } finally {
      setIsCompressing(false);
    }
  }

  function handleRemoveSelectedFile(fileKey: string) {
    setSubmitError("");
    setSelectedFiles((prev) =>
      prev.filter((selected) => selected.sourceKey !== fileKey),
    );
  }

  async function handleOrderFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;

    // Honeypot trap: bots usually fill hidden fields.
    if (orderFormData.website.trim() !== "") {
      setSubmitError("Unable to submit this form. Please try again.");
      return;
    }

    if (!isValidEmail(orderFormData.email)) {
      setSubmitError(
        "Please enter a valid email address (e.g. name@example.com).",
      );
      return;
    }

    if (!isCompletePhoneNumber(orderFormData.phone)) {
      setSubmitError("Please enter a valid phone number.");
      return;
    }

    if (!isSelectableFormDate(orderFormData.date)) {
      setSubmitError("Please choose a valid date that is today or later.");
      return;
    }

    if (
      orderFormData.pickupDelivery === "Delivery" &&
      !orderFormData.deliveryAddress.trim()
    ) {
      setSubmitError("Please enter a delivery address.");
      return;
    }

    setSubmitError("");
    setSubmitSuccess("");
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("formType", "order");
      Object.entries(orderFormData).forEach(([key, value]) => {
        if (
          key === "deliveryAddress" &&
          orderFormData.pickupDelivery !== "Delivery"
        ) {
          return;
        }
        formData.append(
          key,
          key === "phone" ? formatPhoneForSubmit(value) : value,
        );
      });
      selectedFiles.forEach(({ file }) => {
        formData.append("referenceImages", file, file.name);
      });

      await submitWorkerMultipartForm(formData);

      setSubmitSuccess(
        "Thank you! Your order inquiry was submitted successfully.",
      );
      setOrderFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        date: "",
        celebrationTime: "",
        pickupDelivery: "Pickup",
        timeNeeded: "",
        deliveryAddress: "",
        subject: "",
        details: "",
        website: "",
      });
      setSelectedFiles([]);
      setPhoneResetKey((key) => key + 1);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      formEl.reset();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Could not submit right now. Please try again in a few minutes.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Form
      id="order-form"
      className="order-form"
      onSubmit={handleOrderFormSubmit}
    >
      <div>
        <label htmlFor="first-name">
          First Name<span className="required">*</span>
        </label>
        <input
          type="text"
          id="first-name"
          name="firstName"
          onChange={handleOrderFormChange}
          required
        />
      </div>
      <div>
        <label htmlFor="last-name">
          Last Name<span className="required">*</span>
        </label>
        <input
          type="text"
          id="last-name"
          name="lastName"
          onChange={handleOrderFormChange}
          required
        />
      </div>
      <div>
        <label htmlFor="email">
          Email<span className="required">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          autoComplete="email"
          pattern={EMAIL_INPUT_PATTERN}
          onChange={handleOrderFormChange}
          onInvalid={(e) => applyEmailInputValidity(e.currentTarget)}
          required
        />
      </div>
      <div>
        <label htmlFor="phone">
          Phone<span className="required">*</span>
        </label>
        <PhoneInputField
          id="phone"
          name="phone"
          value={orderFormData.phone}
          onChange={(phone) => setOrderFormData((prev) => ({ ...prev, phone }))}
          reset={phoneResetKey}
        />
      </div>
      <div>
        <label htmlFor="date">
          Date of Celebration<span className="required">*</span>
        </label>
        <input
          type="date"
          id="date"
          name="date"
          min={getLocalDateString()}
          onChange={handleOrderFormChange}
          required
        />
      </div>
      <div>
        <label htmlFor="celebration-time">
          Time of Celebration<span className="required">*</span>
        </label>
        <input
          type="text"
          id="celebration-time"
          name="celebrationTime"
          onChange={handleOrderFormChange}
          required
        />
      </div>
      <div>
        <label htmlFor="pickup-delivery">
          For Pickup or Delivery<span className="required">*</span>
        </label>
        <select
          id="pickup-delivery"
          name="pickupDelivery"
          value={orderFormData.pickupDelivery}
          onChange={handleOrderFormChange}
          required
        >
          <option value="Pickup">Pickup (Mountain View)</option>
          <option value="Delivery">
            Delivery (Cost dependent on location)
          </option>
        </select>
      </div>
      <div>
        <label htmlFor="time-needed">
          Requested Pickup / Delivery Time<span className="required">*</span>
        </label>
        <input
          type="text"
          id="time-needed"
          name="timeNeeded"
          onChange={handleOrderFormChange}
          required
        />
      </div>
      <div className="full-width">
        <label htmlFor="delivery-address">
          Delivery Address
          {orderFormData.pickupDelivery === "Delivery" ? (
            <span className="required">*</span>
          ) : null}
        </label>
        <input
          type="text"
          id="delivery-address"
          name="deliveryAddress"
          value={orderFormData.deliveryAddress}
          onChange={handleOrderFormChange}
          disabled={orderFormData.pickupDelivery !== "Delivery"}
          required={orderFormData.pickupDelivery === "Delivery"}
        />
      </div>
      <div className="full-width">
        <label htmlFor="subject">Subject</label>
        <input
          type="text"
          id="subject"
          name="subject"
          onChange={handleOrderFormChange}
        />
      </div>
      <div className="full-width">
        <label htmlFor="details">
          Details<span className="required">*</span>
        </label>
        <p>
          Please include any details that will help in finalizing your order:
          <ul className="details__list">
            <li>Guest count / quantity or sizing desired</li>
            <li>Cake/cupcake flavors</li>
            <li>Design requests</li>
          </ul>
        </p>
        <p>
          Feel free to link to any inspiration photos online or any references
          from my{" "}
          <a
            href="https://www.instagram.com/conniescakerie"
            target="_blank"
            rel="noopener noreferrer"
          >
            Instagram
          </a>
          .
        </p>
        <textarea
          id="details"
          name="details"
          onChange={handleOrderFormChange}
          required
        />
      </div>
      <div className="full-width">
        <label htmlFor="reference-images">Reference Files (optional)</label>
        <div className="file-picker">
          <input
            ref={fileInputRef}
            id="reference-images"
            name="referenceImages"
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,image/jpeg,image/png,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleFileChange}
            className="file-picker__input"
          />
          <div className="file-picker__top">
            <button
              className="file-picker__btn"
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isCompressing || isSubmitting}
            >
              {isCompressing ? "Optimizing..." : "Choose Files"}
            </button>
            <span className="file-picker__native-label">
              {isCompressing
                ? "Resizing images to keep email attachments small. "
                : selectedFiles.length > 0
                  ? `${selectedFiles.length} file(s) selected. `
                  : "No file chosen. "}
              Up to 8 files. JPG/PNG/WEBP/PDF/DOC/DOCX.
            </span>
          </div>
          {selectedFiles.length > 0 && (
            <ul className="selected-files-list">
              {selectedFiles.map(({ sourceKey, file }) => (
                <li key={sourceKey}>
                  <span className="selected-files-list__name">{file.name}</span>
                  <button
                    type="button"
                    className="selected-files-list__remove"
                    onClick={() => handleRemoveSelectedFile(sourceKey)}
                    aria-label={`Remove ${file.name}`}
                    disabled={isCompressing || isSubmitting}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="honeypot-field" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="website"
          value={orderFormData.website}
          onChange={handleOrderFormChange}
          autoComplete="off"
          tabIndex={-1}
        />
      </div>
      <button
        className="btn"
        type="submit"
        disabled={isSubmitting || isCompressing}
      >
        {isSubmitting ? "Submitting..." : "Submit"}
      </button>
      {(submitError || submitSuccess) && (
        <p className="form-status">{submitError || submitSuccess}</p>
      )}
    </Form>
  );
}

export default OrderForm;
